import { Component, OnInit, Injectable, Inject, Input, Output, EventEmitter, Optional } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { AccessDomain } from 'libs/portal/src/models/AccessDomain';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { Endpoints } from '@huge-monorepo/portal/config';

export class RoleNode {
  children: RoleNode[];
  Description: string;
  ID: number;
  AccessDomainRoleID?: number;
}

/** Flat to-do Description node with expandable and level information */
export class FlatRoleNode {
  Description: string;
  level: number;
  ID: number;
  AccessDomainRoleID?: number;
  expandable: boolean;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class UserRoleChecklist {
  dataChange = new BehaviorSubject<RoleNode[]>([]);

  get data(): RoleNode[] {
    return this.dataChange.value;
  }

  constructor() {
    this.initialize({});
  }

  initialize(obj: any) {
    // Build the tree nodes from Json object. The result is a list of `RoleNode` with nested
    //     file node as children.
    const data = this.buildFileTree(obj, 0);
    // console.log(data);
    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `RoleNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): RoleNode[] {
    return Object.keys(obj).reduce<RoleNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new RoleNode();
      node.Description = key;
      node.AccessDomainRoleID = parseInt(key);
      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.Description = value;
        }
      }
      return accumulator.concat(node);
    }, []);
  }
}

@Component({
  selector: 'app-user-role-tree',
  templateUrl: './user-role-tree.component.html',
  styleUrls: ['./user-role-tree.component.scss'],
  providers: [UserRoleChecklist],
})
export class UserRoleTreeComponent implements OnInit {
  userRoles: AccessDomain[];
  @Input() preSelected: AccessDomain[];
  @Input() userID: number;
  @Input() ReadOnly: boolean;
  selectedRoles: any = [];
  @Output() selectedRolesEmitter: EventEmitter<any> = new EventEmitter<any>();
  loading = true;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<FlatRoleNode, RoleNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<RoleNode, FlatRoleNode>();

  /** A selected parent node to be inserted */
  selectedParent: FlatRoleNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<FlatRoleNode>;

  treeFlattener: MatTreeFlattener<RoleNode, FlatRoleNode>;

  dataSource: MatTreeFlatDataSource<RoleNode, FlatRoleNode>;
  _database: UserRoleChecklist;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<FlatRoleNode>(true /* multiple */);

  ngOnInit() {
    this.getUserRoles();
  }

  constructor(
    private db: UserRoleChecklist,
    @Optional() public dialogRef: MatDialogRef<UserRoleTreeComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: AccessDomain[],
    private genericService: GenericService,
    private endpoints: Endpoints,
  ) {
    this._database = db;
    if (data != null) {
      this.preSelected = data['Roles'];
      this.userID = data['UserID'];
      this.ReadOnly = data['ReadOnly'] === true ? true : false;
    }
  }

  buildTree(data: Object) {
    this._database.initialize(data);
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<FlatRoleNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this._database.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
    this.treeControl.expandAll();
  }

  getLevel = (node: FlatRoleNode) => node.level;

  isExpandable = (node: FlatRoleNode) => node.expandable;

  getChildren = (node: RoleNode): RoleNode[] => node.children;

  hasChild = (_: number, _nodeData: FlatRoleNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: FlatRoleNode) => _nodeData.Description === '';

  bubbleSort(list: AccessDomain[]): AccessDomain[] {
    let swapp;
    let n = list.length - 1;
    const x = list;
    do {
      swapp = false;
      for (let i = 0; i < n; i++) {
        if (x[i].ID > x[i + 1].ID) {
          const temp = x[i];
          x[i] = x[i + 1];
          x[i + 1] = temp;
          swapp = true;
        }
      }
      n--;
    } while (swapp);
    return x;
  }

  getUserRoles() {
    this.genericService.GET(this.endpoints.GET_DOMAIN_ROLES).subscribe(
      (result) => {
        // console.log(result);
        if (result['body'] == null) {
          result['body'] = [];
        }

        this.userRoles = this.bubbleSort(result['body'] as AccessDomain[]);
        console.log(this.userRoles);
        const obj = {};
        this.userRoles.forEach((domain) => {
          const roleObj = {};
          domain['Roles'].forEach((role) => {
            roleObj[role['AccessDomainRoleID']] = role['Description'];
          });
          obj[domain['Description']] = roleObj;
        });
        this.buildTree(obj);
        if (this.preSelected != null) {
          for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
            if (this.treeControl.dataNodes[i].level == 0) {
              // This is a domain
              this.preSelected.forEach((pre) => {
                if (this.treeControl.dataNodes[i].Description == pre.Description) {
                  pre.Roles.forEach((preRole) => {
                    for (let j = i + 1; j < this.treeControl.dataNodes.length; j++) {
                      if (this.treeControl.dataNodes[j].level == 0) {
                        break;
                      }
                      if (preRole.Description == this.treeControl.dataNodes[j].Description) {
                        this.todoLeafItemSelectionToggle(this.treeControl.dataNodes[j]);
                      }
                    }
                  });
                }
              });
            }
          }
        }
        this.loading = false;
      },
      () => {
        console.log('Error in UserRoles GET');
      },
    );
  }

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: RoleNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.Description === node.Description ? existingNode : new FlatRoleNode();
    flatNode.Description = node.Description;
    flatNode.ID = node.ID;
    flatNode.AccessDomainRoleID = node.AccessDomainRoleID;
    flatNode.level = level;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: FlatRoleNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) => this.checklistSelection.isSelected(child));
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: FlatRoleNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: FlatRoleNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.every((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: FlatRoleNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.selectedRoles = this.checklistSelection.selected;
    this.selectedRolesEmitter.emit(this.selectedRoles);
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: FlatRoleNode): void {
    let parent: FlatRoleNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: FlatRoleNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) => this.checklistSelection.isSelected(child));
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: FlatRoleNode): FlatRoleNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  cancel() {
    this.dialogRef.close();
  }

  save() {
    const selected = [];
    this.checklistSelection.selected.forEach((element) => {
      if (!isNaN(element.AccessDomainRoleID)) {
        selected.push(element.AccessDomainRoleID);
      }
    });
    console.log(selected);
    this.genericService.PUT('/users/' + this.userID + '/accessroles/', selected).subscribe(
      () => {
        // this.toastr.success(
        //   "The user's roles have successfully been updated with immediate effect.",
        //   "User Role Update Successful!",
        //   {
        //     extendedTimeOut: 3000,
        //     progressBar: true,
        //   }
        // );
        this.dialogRef.close(true);
      },
      (err) => {
        console.log(err);
      },
    );
  }
}
